import AccountCircleRoundedIcon from '@mui/icons-material/AccountCircleRounded';
import DarkModeOutlinedIcon from '@mui/icons-material/DarkModeOutlined';
import LightModeRoundedIcon from '@mui/icons-material/LightModeRounded';
import LockResetRoundedIcon from '@mui/icons-material/LockResetRounded';
import LogoutRoundedIcon from '@mui/icons-material/LogoutRounded';
import MenuOpenRoundedIcon from '@mui/icons-material/MenuOpenRounded';
import {
  Alert,
  AppBar,
  Box,
  ButtonGroup,
  createTheme,
  CssBaseline,
  Divider,
  Drawer,
  IconButton,
  ListItemIcon,
  Menu,
  MenuItem,
  PaletteMode,
  Paper,
  Snackbar,
  SxProps,
  Theme as MuiTheme,
  ThemeProvider,
  Toolbar,
  Tooltip,
  Typography,
  useMediaQuery,
} from '@mui/material';
import LogRocket from 'logrocket';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Link,
  NavLink,
  Outlet,
  useLocation,
  useNavigate,
} from 'react-router-dom';
import AuthApi from '../apis/AuthApi';
import SetupApi from '../apis/SetupApi';
import Footer from '../components/Footer/Footer';
import LoadingCircle from '../components/LoadingCircle/LoadingCircle';
import Logo from '../components/Logo/Logo';
import NavButton from '../components/NavButton/NavButton';
import NavButtons from '../components/NavButtons/NavButtons';
import FooterContext, {
  FooterStateContextType,
} from '../contexts/FooterStateContext';
import PaletteModeContext from '../contexts/PaletteModeContext';
import SnackbarContext, {
  SnackbarContextType,
} from '../contexts/SnackbarContext';
import { ChangePasswordDto } from '../generated-types/changePasswordDto';
import { CreateAccountDto } from '../generated-types/createAccountDto';
import { EnvironmentBarDto } from '../generated-types/environmentBarDto';
import { ResetPasswordDto } from '../generated-types/resetPasswordDto';
import { UserLoginDto } from '../generated-types/userLoginDto';
import { APP_BAR_HEIGHT, ENV_BAR_HEIGHT } from '../lib/constants';
import { nameof } from '../lib/nameof';
import useApi from '../lib/useApi';
import useSharedAuthLib from '../lib/useSharedAuthLib';
import Theme from '../theme/Theme';
import { ChangePasswordWithConfirmDto } from '../views/ChangePasswordView/ChangePasswordView';
import { ResetPasswordWithConfirmDto } from '../views/ForgotPasswordView/components/ResetPasswordForm';
import { CreateAccountWithConfirmDto } from '../views/SignupView/SignupView';

/**
 * The layout for the s3g application. At this level, the top bar exists, as well as the palette mode control
 */
const Layout = () => {
  const paletteModeStorageKey = 'paletteMode';
  const [paletteMode, setPaletteMode] = useState<PaletteMode>(
    (window.localStorage.getItem(paletteModeStorageKey) as PaletteMode) ??
      'light'
  );
  const [alert, setAlert] = useState<JSX.Element>();
  const [showSnackbar, setShowSnackbar] = useState(false);
  const [accountMenuAnchor, setAccountAnchorMenu] =
    useState<null | HTMLElement>(null);
  const accountMenuOpen = Boolean(accountMenuAnchor);
  const theme = createTheme(Theme(paletteMode));
  const navigate = useNavigate();
  const [showMobileNav, setShowMobileNav] = useState(false);
  const [showFooter, setShowFooter] = useState(true);
  const [showFooterNav, setShowFooterNav] = useState(true);
  const [logRocketInitialized, setLogRocketInitialized] = useState(false);
  const setupApi = useApi(SetupApi);
  const [envBarDetails, setEnvBarDetails] = useState<EnvironmentBarDto>({
    show: false,
  });
  const authenticating = useRef(false);
  const { pathname } = useLocation();
  const mainId = 'main-id';

  // Auth
  const [initializingAuth, setInitializingAuth] = useState(true);
  const authApi = useApi(AuthApi);
  const authLib = useSharedAuthLib();

  const isMd = useMediaQuery(theme.breakpoints.up('md'), {
    defaultMatches: true,
  });

  const navStyle = useMemo<SxProps<MuiTheme>>(
    () => ({
      fontSize: isMd ? '1em' : '2em',
    }),
    [isMd]
  );

  useEffect(() => {
    // Set the palette
    const storedPaletteMode = window.localStorage.getItem(
      paletteModeStorageKey
    ) as PaletteMode;
    if (storedPaletteMode) {
      setPaletteMode(storedPaletteMode);
    }
  }, []);

  /** Initialize the authentication */
  useEffect(() => {
    if (authenticating.current) return; // Prevent second execution in StrictMode
    authenticating.current = true;

    if (authLib.AuthState.Authenticated) {
      setInitializingAuth(false);
      return; // Already authenticated
    }

    // Try to set the authentication from the refresh token
    setInitializingAuth(true);
    authLib.RefreshToken(true).finally(() => setInitializingAuth(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // Load the log rocket configuration
    if (!logRocketInitialized && window.location.hostname !== 'localhost') {
      setupApi
        .GetLogRocketAppId()
        .then((s) => {
          // Initialize log rocket
          LogRocket.init(s, {
            network: {
              isEnabled: true,
              requestSanitizer: (request) => {
                if (request.body) {
                  const body = JSON.parse(request.body);

                  // List of fields to mask - we're explicit with every field on purpose,
                  //  even if there are duplicates.
                  const fieldsToMask = [
                    // Login
                    nameof<UserLoginDto>((u) => u.password),
                    // Create account
                    nameof<CreateAccountDto>((u) => u.password),
                    nameof<CreateAccountWithConfirmDto>((u) => u.password),
                    nameof<CreateAccountWithConfirmDto>(
                      (u) => u.confirmPassword
                    ),
                    // Change password
                    nameof<ChangePasswordDto>((u) => u.newPassword),
                    nameof<ChangePasswordDto>((u) => u.oldPassword),
                    nameof<ChangePasswordWithConfirmDto>((u) => u.newPassword),
                    nameof<ChangePasswordWithConfirmDto>((u) => u.oldPassword),
                    nameof<ChangePasswordWithConfirmDto>(
                      (u) => u.confirmPassword
                    ),
                    // Reset password
                    nameof<ResetPasswordDto>((u) => u.code),
                    nameof<ResetPasswordDto>((u) => u.newPassword),
                    nameof<ResetPasswordWithConfirmDto>((u) => u.newPassword),
                    nameof<ResetPasswordWithConfirmDto>(
                      (u) => u.confirmPassword
                    ),
                  ];

                  fieldsToMask.forEach((field) => {
                    if (body[field]) {
                      body[field] = '******'; // Mask the sensitive field
                    }
                  });
                  request.body = JSON.stringify(body);
                }
                return request;
              },
            },
          });

          setLogRocketInitialized(true);
        })
        .catch((e) => console.error('Failed to load LogRocket', e));
    }
  }, [logRocketInitialized, setupApi]);

  useEffect(() => {
    setupApi.GetEnvironmentBar().then(setEnvBarDetails);
  }, [setupApi]);

  /** Scroll to the top of the `main` html element when the navigation url changes */
  useEffect(() => {
    const main = document.getElementById(mainId);
    main?.scrollTo({
      top: 0,
      behavior: 'instant',
    });
  }, [pathname]);

  const setMode = useCallback((mode: PaletteMode) => {
    setPaletteMode(mode);
    window.localStorage.setItem(paletteModeStorageKey, mode);
  }, []);

  const snackbarValue = useMemo<SnackbarContextType>(
    () => ({
      triggerSnackbar: () => {
        setShowSnackbar(true);
      },
      setAlert: setAlert,
    }),
    []
  );

  const footerStateValue = useMemo<FooterStateContextType>(
    () => ({
      showFooter: (footerVisibility: boolean) =>
        setShowFooter(footerVisibility),
      showFooterNav: (footerNavVisibility: boolean) =>
        setShowFooterNav(footerNavVisibility),
    }),
    []
  );

  const logoutFlow = useCallback(async () => {
    // Logout with the backend
    try {
      await authApi.Logout();
    } catch (error) {
      setAlert(
        <Alert severity="error" variant="filled">
          Logout was unsuccessful. Please try again. If this continues, please
          contact us.
        </Alert>
      );
      setShowSnackbar(true);
      return;
    }

    // Logout locally
    authLib.Logout();
    navigate('/');
    setAlert(
      <Alert severity="success" variant="filled">
        Successfully logged out.
      </Alert>
    );
    setShowSnackbar(true);
  }, [authApi, authLib, navigate]);

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      {!initializingAuth && (
        <PaletteModeContext.Provider value={paletteMode}>
          <SnackbarContext.Provider value={snackbarValue}>
            <Box
              height="100%"
              display="flex"
              flexDirection="column"
              position="relative"
              overflow="hidden"
            >
              <AppBar
                position="fixed"
                elevation={0}
                sx={{
                  backgroundColor: 'unset',
                  zIndex: (theme) => theme.zIndex.drawer + 1,
                }}
              >
                {envBarDetails.show && (
                  <Box
                    height={`${ENV_BAR_HEIGHT}px`}
                    sx={{
                      textAlign: 'center',
                      backgroundColor:
                        envBarDetails.backgroundColor ?? 'initial',
                      color: envBarDetails.foregroundColor ?? 'initial',
                      fontFamily: 'monospace',
                      fontWeight: 'bold',
                      paddingY: '.1em',
                      fontSize: '.9em',
                    }}
                  >
                    {envBarDetails.text}
                  </Box>
                )}
                <Toolbar
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between',
                    padding: '0 !important',
                    zIndex: (theme) => theme.zIndex.drawer + 1,
                    height: APP_BAR_HEIGHT,
                  }}
                >
                  <Paper
                    elevation={0}
                    square
                    sx={{
                      height: '100%',
                      width: '100%',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      padding: '.5em',
                      position: 'relative',
                      zIndex: 1000,
                    }}
                  >
                    <Box
                      id="logo-nav-button"
                      component={Link}
                      to="/"
                      display="inline-flex"
                      marginLeft=".75em"
                      sx={{
                        textDecoration: 'inherit',
                        color: 'inherit',
                      }}
                    >
                      <Logo sizeEm={2} showText />
                    </Box>
                    <nav
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: isMd ? '1em' : '.5em',
                      }}
                    >
                      {isMd && <NavButtons />}
                      <Tooltip
                        arrow
                        title={`Toggle ${
                          paletteMode == 'light' ? 'dark' : 'light'
                        } mode`}
                      >
                        <IconButton
                          onClick={() =>
                            setMode(paletteMode == 'dark' ? 'light' : 'dark')
                          }
                          sx={{
                            marginRight: {
                              xs: 0,
                              md: '-.25em',
                            },
                          }}
                        >
                          {paletteMode == 'light' ? (
                            <DarkModeOutlinedIcon
                              sx={{ color: theme.palette.secondary.main }}
                            />
                          ) : (
                            <LightModeRoundedIcon sx={{ color: '#ffea41' }} />
                          )}
                        </IconButton>
                      </Tooltip>
                      {!isMd && (
                        <Tooltip arrow title="Navigation">
                          <IconButton
                            color="secondary"
                            onClick={() => setShowMobileNav(!showMobileNav)}
                          >
                            {showMobileNav ? (
                              <MenuOpenRoundedIcon
                                sx={{ transform: 'rotate(180deg)' }}
                              />
                            ) : (
                              <MenuOpenRoundedIcon />
                            )}
                          </IconButton>
                        </Tooltip>
                      )}
                      {authLib.AuthState.Authenticated ? (
                        <>
                          <Tooltip arrow title="Account Menu">
                            <IconButton
                              id="account-menu-button"
                              color={
                                theme.palette.mode == 'light'
                                  ? 'primary'
                                  : 'secondary'
                              }
                              onClick={(e) =>
                                setAccountAnchorMenu(e.currentTarget)
                              }
                              aria-controls={
                                accountMenuOpen ? 'account-menu' : undefined
                              }
                              aria-haspopup="true"
                              aria-expanded={
                                accountMenuOpen ? 'true' : undefined
                              }
                            >
                              <AccountCircleRoundedIcon />
                            </IconButton>
                          </Tooltip>
                        </>
                      ) : (
                        <ButtonGroup
                          sx={{
                            display: {
                              xs: 'none',
                              md: 'initial',
                            },
                          }}
                          color="primary"
                        >
                          <NavButton
                            id="login-nav-button"
                            to="/login"
                            component={NavLink}
                            variant="contained"
                            color="primary"
                          >
                            Login
                          </NavButton>
                          <NavButton
                            id="signup-nav-button"
                            to="/signup"
                            component={NavLink}
                            variant="contained"
                            color="primary"
                          >
                            Signup
                          </NavButton>
                        </ButtonGroup>
                      )}
                    </nav>
                  </Paper>
                  <Divider sx={{ width: '100%' }} />
                </Toolbar>
              </AppBar>
              <FooterContext.Provider value={footerStateValue}>
                <main
                  id={mainId}
                  style={{
                    height: '100%',
                    overflow: 'auto',
                    display: 'flex',
                    marginTop: `${
                      APP_BAR_HEIGHT + (envBarDetails.show ? ENV_BAR_HEIGHT : 0)
                    }px`,
                  }}
                >
                  <Outlet />
                </main>
                {showFooter && <Footer showNav={showFooterNav} />}
              </FooterContext.Provider>
              <Drawer
                variant="temporary"
                anchor="right"
                open={showMobileNav}
                onClick={() => setShowMobileNav(false)}
                PaperProps={{
                  elevation: 0,
                }}
              >
                <nav
                  style={{
                    display: isMd ? 'none' : 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between',
                    gap: '1em',
                    padding: '1em',
                    height: '100%',
                  }}
                >
                  <Box display="flex" flexDirection="column" gap="1em">
                    <Toolbar />
                    {!authLib.AuthState.Authenticated && (
                      <>
                        <NavButton
                          to="/login"
                          component={NavLink}
                          sx={navStyle}
                        >
                          Login
                        </NavButton>
                        <NavButton
                          to="/signup"
                          component={NavLink}
                          sx={navStyle}
                        >
                          Signup
                        </NavButton>
                      </>
                    )}
                    <NavButtons />
                  </Box>
                  <Typography
                    marginTop={2}
                    align={'center'}
                    variant={'subtitle2'}
                    color="textSecondary"
                    marginLeft={1}
                  >
                    &copy; {new Date().getFullYear()}, Strategic Security
                    Solutions Group LLC. All rights reserved.
                  </Typography>
                </nav>
              </Drawer>
            </Box>
            <Snackbar
              open={showSnackbar}
              autoHideDuration={6000}
              onClose={() => {
                setShowSnackbar(false);
              }}
              sx={{
                zIndex: 100000,
              }}
            >
              {alert}
            </Snackbar>
            <Menu
              anchorEl={accountMenuAnchor}
              id="account-menu"
              open={accountMenuOpen}
              onClick={() => setAccountAnchorMenu(null)}
              onClose={() => setAccountAnchorMenu(null)}
            >
              <MenuItem
                id="change-password-button"
                onClick={() => navigate('/change-password')}
              >
                <ListItemIcon>
                  <LockResetRoundedIcon />
                </ListItemIcon>
                Change password
              </MenuItem>
              <MenuItem onClick={logoutFlow}>
                <ListItemIcon>
                  <LogoutRoundedIcon />
                </ListItemIcon>
                Logout
              </MenuItem>
            </Menu>
          </SnackbarContext.Provider>
        </PaletteModeContext.Provider>
      )}
      <LoadingCircle isLoading={initializingAuth} loadingDelayMs={0} />
    </ThemeProvider>
  );
};

export default Layout;
